<script setup lang="ts">
import { useCookie } from '#imports'
import FullProduct from '~/models/Product/FullProduct'
import WatchdogProductResponse from '~/models/Api/WatchdogProductResponse'

const props = defineProps({
    productData: {
        type: Object as PropType<FullProduct>,
        required: true,
    },
})

const watchdogHash = ref<string | null>(null)
const renewResult = ref<WatchdogProductResponse | null>(null)
watchdogHash.value = (useRoute()?.query.watchdog as string) ?? null

const cookieName = 'watchdog'
const cookieData = useCookie<{ productIds: number[] } | undefined>('watchdog').value
const productIds = ref<number[]>(cookieData?.productIds || [])

if (watchdogHash.value !== null) {
    productIds.value = productIds.value.filter((id) => id !== props.productData.id)
    useCookie(cookieName).value = JSON.stringify({
        productIds: productIds.value,
    })
}

const isSubscribedToProduct = computed(() => {
    return productIds.value.includes(props.productData.id)
})

const showWatchdogModal = () => {
    useNuxtApp().$bus.$emit('showModal', 'watchdog-product-modal')
}

const renewWatchdog = async (renew: boolean) => {
    if (!watchdogHash.value) {
        return
    }

    renewResult.value = await useNuxtApp().$repository.renewWatchdogProduct(watchdogHash.value, renew)

    if (renewResult.value.upserted && renew) {
        productIds.value.push(props.productData.id)
        useCookie(cookieName).value = JSON.stringify({
            productIds: productIds.value,
        })
    }

    if (!renew) {
        const route = useRoute()
        watchdogHash.value = null

        safeNavigateTo(route.path)
    }
}
</script>

<template>
    <div class="watchdog">
        <template v-if="!watchdogHash">
            <button v-if="!isSubscribedToProduct" class="btn btn-primary" type="button" @click="showWatchdogModal()">
                {{ $t('watchdog.ctaModal') }}
            </button>
        </template>
        <template v-else>
            <div v-if="!isSubscribedToProduct" class="watchdog__message">
                <span v-html="$t('watchdog.late')"></span>
                <div class="watchdog__message__btn">
                    <button class="btn btn-primary positive-button" type="button" @click="renewWatchdog(true)">
                        {{ $t('watchdog.yes') }}
                    </button>
                    <button class="btn btn-outline-primary negative-button" type="button" @click="renewWatchdog(false)">
                        {{ $t('watchdog.no') }}
                    </button>
                </div>
            </div>
        </template>
        <UiAlert v-if="isSubscribedToProduct" color="green" text-colored>
            <img src="/nuxt-assets/images/icons/check-green.svg" class="check" width="16" height="16" alt="check" />
            <div class="">
                {{ $t('watchdog.watchdogProductUpserted') }}
            </div>
        </UiAlert>
    </div>
</template>

<style scoped lang="scss">
.watchdog {
    margin-top: 1rem;

    button {
        padding: 8px 20px 8px 20px;
        width: 50%;
    }

    &__message {
        border-radius: $border-radius;
        padding: 1rem;
        background-color: $color-grey;
        color: $color-dark;
        font-size: 13px;
        font-weight: 590;

        @include screen-md {
            font-size: 15px;
        }

        &__btn {
            display: flex;
            gap: 10px;
            margin-top: 0.5rem;

            button {
                padding: 5px 20px 5px 20px;

                @include screen-md {
                    padding: 8px 20px 8px 20px;
                }
            }

            .positive-button {
                width: 70%;
            }
        }
    }
}
</style>
